import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChatCircleDots, X, PaperPlaneTilt, Sparkle, CalendarPlus, House } from "@phosphor-icons/react";
import api from "@/lib/apiClient";
import { BRAND } from "@/lib/constants";

const SESSION_KEY = "wg_concierge_session";
const GREETING =
  "Hi! I'm Lamar's AI concierge. Buying, selling, or renting in New York? I can help with any of them — just tell me what you're after.";

function Bubble({ role, children }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid="concierge-message">
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "bg-brand text-black rounded-br-sm"
            : "bg-white/[0.07] text-white/90 rounded-bl-sm"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export default function ConciergeChat() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [teaser, setTeaser] = useState(false);
  const scrollRef = useRef(null);

  const hidden = location.pathname.startsWith("/dashboard") || location.pathname === "/login";

  // Proactive nudge: pop a teaser ~15s in (once per browser session) to lift engagement.
  useEffect(() => {
    if (open || hidden) return;
    if (sessionStorage.getItem("wg_concierge_teased")) return;
    const t = setTimeout(() => setTeaser(true), 15000);
    return () => clearTimeout(t);
  }, [open, hidden]);

  const dismissTeaser = (e) => {
    if (e) e.stopPropagation();
    setTeaser(false);
    sessionStorage.setItem("wg_concierge_teased", "1");
  };

  const openChat = () => {
    setOpen(true);
    dismissTeaser();
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/concierge/chat", { session_id: sessionId, message: text });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry, I'm having trouble right now. Please try the Buy or Sell form and Lamar will reach out." },
      ]);
    } finally {
      setSending(false);
    }
  }, [input, sending, sessionId]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (hidden) return null;

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="concierge-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-5 z-[9998] flex h-[34rem] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0E0F14] shadow-2xl sm:w-96"
          >
            <div className="flex items-center justify-between border-b border-white/10 bg-[#101116] px-4 py-3">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand/15 text-brand">
                  <Sparkle size={16} weight="fill" />
                </span>
                <div className="leading-tight">
                  <p className="text-sm font-medium text-white">{BRAND.name} Concierge</p>
                  <p className="text-[11px] text-green-400">● Online · replies instantly</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="concierge-close" className="text-white/50 hover:text-white" aria-label="Close chat">
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role}>{m.content}</Bubble>
              ))}
              {sending && (
                <Bubble role="assistant">
                  <span className="inline-flex gap-1">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/50 [animation-delay:-0.2s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/50 [animation-delay:-0.1s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/50" />
                  </span>
                </Bubble>
              )}
            </div>

            <div className="border-t border-white/10 bg-[#101116] p-3">
              <div className="mb-2 flex gap-2">
                <button
                  type="button"
                  data-testid="concierge-book-chip"
                  onClick={() => { setOpen(false); navigate("/book"); }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-brand/40 px-3 py-1.5 text-[11px] uppercase tracking-wider text-brand hover:bg-brand/10"
                >
                  <CalendarPlus size={13} weight="bold" /> Book a call
                </button>
                <button
                  type="button"
                  data-testid="concierge-value-chip"
                  onClick={() => { setOpen(false); navigate("/home-value"); }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] uppercase tracking-wider text-white/60 hover:text-brand hover:border-brand/40"
                >
                  <House size={13} weight="bold" /> Home value
                </button>
              </div>
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="concierge-input"
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Type your message…"
                  className="max-h-24 flex-1 resize-none rounded-lg border border-white/10 bg-[#0B0C10] px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-brand/50"
                />
                <button
                  onClick={send}
                  disabled={sending || !input.trim()}
                  data-testid="concierge-send"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand text-black transition-colors hover:bg-brand-hover disabled:opacity-50"
                  aria-label="Send"
                >
                  <PaperPlaneTilt size={16} weight="fill" />
                </button>
              </div>
              <p className="mt-2 text-center text-[10px] text-white/25">AI concierge · for general help, not a substitute for Lamar&apos;s advice</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {teaser && !open && (
          <motion.div
            data-testid="concierge-teaser"
            initial={{ opacity: 0, y: 12, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.95 }}
            onClick={openChat}
            className="fixed bottom-[5.5rem] right-5 z-[9998] max-w-[16rem] cursor-pointer rounded-2xl rounded-br-sm border border-white/10 bg-[#101116] px-4 py-3 shadow-2xl"
          >
            <button
              onClick={dismissTeaser}
              data-testid="concierge-teaser-close"
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-white/10 bg-[#1a1b22] text-white/60 hover:text-white"
              aria-label="Dismiss"
            >
              <X size={13} />
            </button>
            <p className="text-sm text-white/90">👋 Looking to buy or sell? I can help — ask me anything.</p>
            <p className="mt-1 text-[11px] font-medium text-brand">Tap to chat →</p>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => (open ? setOpen(false) : openChat())}
        data-testid="concierge-toggle"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-5 right-5 z-[9999] flex h-14 w-14 items-center justify-center rounded-full bg-brand text-black shadow-2xl shadow-black/40"
        aria-label="Open concierge chat"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <X size={24} weight="bold" />
            </motion.span>
          ) : (
            <motion.span key="chat" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
              <ChatCircleDots size={26} weight="fill" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </>
  );
}
